/** 批量实验矩阵（F-014）：策略 × 种子 × 场景 笛卡尔积，组间隔离、可取消、带进度。 */
import { Simulation } from '../core/engine/simulation';
import type { ExperimentConfig } from '../core/config-schema';
import { ExperimentConfigSchema } from '../core/config-schema';
import type { ExperimentResult } from '../data/export-csv';
import type { Rule } from '../strategy/rule-engine/rule-model';
import { createStrategy } from '../strategy/registry';
import { createRuleStrategy } from '../strategy/rule-engine/rule-strategy';

export interface BatchScene {
  readonly id: string;
  readonly config: ExperimentConfig;
}

export interface BatchMatrix {
  readonly strategies: readonly string[];
  readonly seeds: readonly number[];
  readonly scenes: readonly BatchScene[];
  readonly rules?: readonly Rule[];
}

export interface BatchProgress {
  done: number;
  total: number;
  lastLabel: string;
}

function makeFactory(strategyId: string, rules: readonly Rule[] | undefined): () => import('../strategy/strategy-api').DispatchStrategy {
  return () => (strategyId === 'rule-engine' ? createRuleStrategy(rules ?? []) : createStrategy(strategyId));
}

/**
 * 运行批量矩阵。每组在独立内核实例中运行，单组失败隔离（AC-F014-02）；
 * 组间 yield 保持 UI 响应并上报进度。返回每组一行结果。
 */
export async function runBatch(
  matrix: BatchMatrix,
  onProgress?: (p: BatchProgress) => void,
  shouldCancel?: () => boolean,
): Promise<ExperimentResult[]> {
  const combos: { strategy: string; seed: number; scene: BatchScene }[] = [];
  for (const strategy of matrix.strategies) {
    for (const seed of matrix.seeds) {
      for (const scene of matrix.scenes) {
        combos.push({ strategy, seed, scene });
      }
    }
  }

  const rows: ExperimentResult[] = [];
  let done = 0;
  for (const c of combos) {
    const label = `${c.scene.id}/${c.strategy}/seed${c.seed}`;
    const t0 = performance.now();
    let ok = true;
    let error: string | undefined;
    let summary: ExperimentResult['summary'] | undefined;
    try {
      const cfg = ExperimentConfigSchema.parse({ ...c.scene.config, seed: c.seed });
      const sim = new Simulation();
      sim.init(cfg, makeFactory(c.strategy, matrix.rules));
      const r = sim.run();
      summary = r.summary;
      if (r.strandedCount > 0) {
        ok = false;
        error = `滞留 ${r.strandedCount}`;
      }
    } catch (e) {
      ok = false;
      error = e instanceof Error ? e.message : String(e);
    }
    rows.push({
      id: label,
      label,
      sceneId: c.scene.id,
      strategyId: c.strategy,
      seed: c.seed,
      summary: summary ?? EMPTY_SUMMARY,
      durationMs: Math.round(performance.now() - t0),
      ok,
      error,
    });
    done++;
    onProgress?.({ done, total: combos.length, lastLabel: label });
    await new Promise((r) => setTimeout(r, 0)); // yield
    if (shouldCancel?.()) break;
  }
  return rows;
}

const EMPTY_SUMMARY: ExperimentResult['summary'] = {
  deliveredCount: 0, waitingCount: 0, ridingCount: 0,
  avgWaitMs: 0, maxWaitMs: 0, avgRideMs: 0, maxRideMs: 0,
  throughputPerHour: 0, carUtilization: 0, energyKwh: 0,
};
